import { useEffect, useState } from "react";
import { cn } from "@/utils/string.ts";
import { useMe } from "@/data/user.ts";
import { SpinnerLoader } from "@/components/ui/Loader.tsx";
import { useTranslation } from "react-i18next";
import { useCreateFeedbackRatingMutation } from "@/data/feedback.ts";
import Button from "@edusogno/primitives/button";
import Textarea from "@edusogno/primitives/textarea";
import LoadingButton from "@edusogno/primitives/loading-button";

interface FeedbackRatingProps {
  setShowFeedback: (value: boolean) => void;
}

export default function FeedbackRating({
  setShowFeedback,
}: FeedbackRatingProps) {
  const [rating, setRating] = useState<number | null>(null);
  const { me } = useMe();
  const [step, setStep] = useState(0);
  const [comment, setComment] = useState("");
  const { t } = useTranslation();
  const { mutate, isLoading } = useCreateFeedbackRatingMutation(() => {
    setStep(3);
    setTimeout(() => {
      setShowFeedback(false);
    }, 1500);
  });

  const handleSend = () => {
    if (!rating || !me) return;

    mutate({
      nps_comment: comment,
      nps_feedback: rating,
      token: me.item.user.token,
    });
  };

  useEffect(() => {
    if (!rating) return;

    if (rating < 9 && rating > 5) {
      setStep(1);
    } else if (rating < 6) {
      setStep(2);
    } else {
      handleSend();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rating]);

  const handleBackClick = () => {
    setStep(0);
    setComment("");
  };

  return (
    <div className="mx-auto py-4 left-0 right-0 absolute bottom-1 rounded-[10px] bg-white max-w-5xl  w-full flex flex-col px-[10px] lg:px-[41px] gap-6 justify-center items-center shadow-xl">
      {step === 0 && (
        <>
          <p className="text-[#2D224C] lg:text-[20px] text-[12px] text-start lg:font-semibold flex justify-between w-full">
            {t("feedback.recommend")}
            {isLoading && step === 0 && (
              <span className={"me-2 text-xs text-brand-secondary"}>
                {" "}
                {t("feedback.submitting")}{" "}
                <SpinnerLoader className={"!h-3 !w-3 ml-1"} />
              </span>
            )}
          </p>
          <div className={"h-full w-full  flex flex-col  justify-evenly"}>
            <div className=" grid grid-cols-11  mb-3 gap-2 md:gap-6 2xl:gap-10 items-center">
              {Array.from({ length: 11 }).map((_, r) => (
                <button
                  key={"key" + r}
                  disabled={isLoading}
                  className={cn(
                    "flex items-center md:text-[20px] text-[12px] justify-center aspect-square border border-brand bg-white rounded-full font-semibold",
                    {
                      "bg-brand": rating && r <= rating,
                      "text-white": rating && r <= rating,
                      "opacity-50 cursor-not-allowed": isLoading,
                    },
                  )}
                  onClick={() => setRating(() => r)}
                >
                  {r}
                </button>
              ))}
            </div>
            <div className="flex flex-row justify-between w-full items-end h-fit ">
              <p className="text-[#2D224C] text-[12px] font-regular">
                {t("feedback.not_likely")}
              </p>
              <p className="text-[#2D224C] text-[12px] font-regular">
                {t("feedback.very_likely")}
              </p>
            </div>
          </div>
        </>
      )}
      {(step === 1 || step === 2) && (
        <>
          <p className="text-brand lg:text-[20px] text-[12px] text-start lg:font-semibold  w-full">
            {step === 1 ? t("feedback.why_high") : t("feedback.why_low")}
          </p>

          <div
            className={"flex flex-col md:flex-row w-full items-center gap-4"}
          >
            <div className={"flex-grow w-full"}>
              <Textarea
                placeholder={t("feedback.comment_placeholder")}
                onChange={(event) => {
                  setComment(event.target.value);
                }}
              />
            </div>
            <div className={"flex md:flex-col gap-2 w-full md:w-auto"}>
              <Button
                size={"md"}
                disabled={isLoading}
                variant="contained"
                onClick={handleBackClick}
              >
                {t("btns.back")}
              </Button>
              <LoadingButton
                size={"md"}
                loading={isLoading}
                disabled={!comment || isLoading}
                variant="contained"
                color={"green"}
                onClick={handleSend}
              >
                {t("btns.send")}
              </LoadingButton>
            </div>
          </div>
        </>
      )}

      {step === 3 && (
        <p className="text-brand lg:text-[20px] text-[14px]  text-center lg:font-semibold  w-full">
          {t("feedback.thanks")}
        </p>
      )}
    </div>
  );
}
